import {defineStore} from "pinia";
import {useSharedStore} from "@/entrypoints/stores/shared";
import {usePresentationStore} from "@/entrypoints/stores/presentation";
import {useVotingModulesStore} from "@/entrypoints/stores/voting_modules";
import {AVCrypto} from "@assemblyvoting/av-crypto"
import apiClient from "@/entrypoints/frontend/apiClient";
import {VoterChannelSubscription, voterChannelSubscription} from "@/channels/VoterChannel";
import {reactive, ref} from "vue";
import type {VotingE2E, VotingVoter} from "@/types";
import {LoginsApiClient, type LoginsApiClientParams} from "@/lib/voting/loginsApiClient";
import i18n from '@/entrypoints/shared/i18n';

export const useVotingSessionStore = defineStore("votingSession", () => {
  const sharedStore = useSharedStore();
  const presentationStore = usePresentationStore();
  const votingModulesStore = useVotingModulesStore();

  // State
  const voting = ref<boolean>(false);
  const voterSessionToken = ref<string>(null);
  const voter = reactive<VotingVoter>({
    id: null,
    identifier: null,
    weight: 0,
    votedOn: [],
    canVoteOn: [],
    name: "",
    voterGroupReference: null,
    demo: false,
    participating: null,
  });
  const authenticateWith = ref<{ electionCodes?: string[], loginsClient?: LoginsApiClientParams }>({})
  const voterSessionUuid = ref<string>(null);
  const redoVote = ref<boolean>(false);
  const expireReason = ref<string>(null);
  const e2e = reactive<VotingE2E>({
    contests: [],
  });

  // Actions
  const setVoting = (payload: boolean) => voting.value = payload;

  const setRedoVote = (payload: boolean) => {
    redoVote.value = payload;
  }

  const updateVoter = (newVoter: VotingVoter) => {
    voter.id = newVoter.id;
    voter.identifier = newVoter.identifier;
    voter.name = newVoter.name;
    voter.weight = newVoter.weight;
    voter.canVoteOn = newVoter.canVoteOn;
    voter.demo = newVoter.demo;
    voter.voterGroupReference = newVoter.voterGroupReference;
    voter.participating = newVoter.participating;
  }

  async function authenticateByElectionCodes(electionCodes: string[], curveName: string) {
    // TODO: Fix this: fetch curve name from some context
    const avCrypto = new AVCrypto(curveName);
    const keyPair = avCrypto.generateProofOfElectionCodes(electionCodes)
    const signature = avCrypto.sign("", keyPair.privateKey);

    try {
      const response = await apiClient.post(`${sharedStore.electionUrl}/voter_sessions`, {
        voter_session: {
          voter_public_key: keyPair.publicKey,
          signature: signature,
        }
      });

      authenticateWith.value.electionCodes = electionCodes;

      return succeedAuthentication(response.data);
    } catch (e) {
      return failAuthentication(e);
    }
  }

  async function authenticateByLoginsClient(loginsApiClientParams: LoginsApiClientParams) {
    try {
      const identityToken = await new LoginsApiClient(loginsApiClientParams).requestIdentityToken();

      const response = await apiClient.post(`${sharedStore.electionUrl}/voter_sessions`, {
        voter_session: { identity_token: identityToken }
      });

      authenticateWith.value.loginsClient = loginsApiClientParams;

      return succeedAuthentication(response.data);
    } catch (e) {
      return failAuthentication(e);
    }
  }

  function succeedAuthentication(data: {
    voter_session_uuid: string,
    voter_session_token: string,
    cable_token: string,
    voter: VotingVoter,
  }) {
    updateVoter(data.voter);
    voterSessionToken.value = data.voter_session_token;
    voterSessionUuid.value = data.voter_session_uuid;
  }

  function failAuthentication(error: any) {
    if (error.response) {
      return error.response.data.error;
    }

    console.error(error);
    // TODO: return some kind of response in the other cases?
  }

  const subscribeToVoterChannel = async () => {
    const cableToken = (await apiClient.get(`${sharedStore.electionUrl}/cable_token`, {
      headers: {
        Authorization: `Bearer ${voterSessionToken.value}`,
      }
    })).data;
    voterChannelSubscription.value = new VoterChannelSubscription(voterSessionUuid.value, cableToken, {
      received: function(data: any) {
        switch (data.type) {
          case "update_voter":
            updateVoter(data.voter);
            break;
          case "session_expired":
            data.expire_reason
              ? destroySession(data.expire_reason)
              : destroySession();
            break;
        }
      },
    });
  }

  const destroySession = (reason?: string) => {
    presentationStore.setVisibleTab("Presentation");
    voterChannelSubscription.value?.unsubscribe();
    resetState();
    votingModulesStore.setPosts([]);
    presentationStore.setSlides([]);

    // Return to logins to sign out there
    if (authenticateWith.value.loginsClient) {
      const loginsClient = new LoginsApiClient(authenticateWith.value.loginsClient);
      let reasonMessage = reason && i18n.global.t(`js.expire_reasons.${reason}`);
      location.assign(loginsClient.signOutUrl(reasonMessage));
    } else if (reason) {
      expireReason.value = reason; // shows a modal to the user with the reason
    }
  }

  const resetState = () => {
    voter.id = null;
    voter.identifier = null;
    voter.weight = 0;
    voter.votedOn = [];
    voter.canVoteOn = [];
    voter.name = "";
    voter.voterGroupReference = null;
    voter.demo = false;
    voter.participating = null;
    voterSessionUuid.value = null;
    voterSessionToken.value = null;
    redoVote.value = false;
    e2e.contests = [];
  }

  const resetExpireReason = () => expireReason.value = null;

  async function prolongSession() {
    await apiClient.put(`${sharedStore.electionUrl}/prolong`, null, {
      headers: {
        Authorization: `Bearer ${voterSessionToken.value}`,
      },
    });
    if (authenticateWith.value.loginsClient) {
      try {
        await new LoginsApiClient(authenticateWith.value.loginsClient).prolongSession();
      } catch (e) {
        destroySession("session_expired");
        console.debug(e)
      }
    }
  }

  const fetchVotedOn = async () => {
    const response = await apiClient.get(`${sharedStore.election.boardUrl}/statistics/votes/voted_on?voter_identifier=${voter.identifier}`);
    if (response.status === 200) {
      response.data.voting_round_references.forEach((votingRoundReference: string) => {
        setVoted(votingRoundReference);
      });
    }
  }

  const reportVoted = async (ballotId: number) => {
    const config = {
      ballotId: ballotId,
    };

    return await apiClient.post(`${sharedStore.electionUrl}/report_voted`, config, { headers: { Authorization: `Bearer ${voterSessionToken.value}` } });
  }

  const setVoted = (votingRoundReference: string) => {
    if (voter.votedOn.includes(votingRoundReference)) return;
    voter.votedOn.push(votingRoundReference);
  }

  return {
    authenticateWith,
    voting,
    setVoting,
    voter,
    updateVoter,
    voterSessionUuid,
    voterSessionToken,
    setRedoVote,
    destroySession,
    subscribeToVoterChannel,
    prolongSession,
    expireReason,
    resetExpireReason,
    authenticateByElectionCodes,
    authenticateByLoginsClient,
    fetchVotedOn,
    reportVoted,
    setVoted,
  }
}, {
  persist: {
    storage: sessionStorage,
    paths: [
      "authenticateWith",
      "voting",
      "voterSessionToken",
      "voter",
      "voterSessionUuid",
      "e2e",
      "expireReason",
      "redoVote",
    ],
  },
});
